import {
  Avatar,
  Skeleton,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Fragment } from 'react'
import { IconButton, SkeletonText } from 'utoniq-ui'

import {
  EventCollectionPickupTimeFragment,
  Maybe,
  PickupTimeItemsAggregationDetailFragment,
} from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { TableScrollFetcher } from '@/components/organisms/functionals'
import { FilterForm } from '@/components/pages/ConsoleQuantityManagements/template/FilterForm'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  artistId: string
  loading: boolean
  pickupDate: Date
  eventName: string
  eventCollection?: EventCollectionPickupTimeFragment
  artistAggregatePickupTimeItems: Maybe<
    (
      | PickupTimeItemsAggregationDetailFragment['pickupTime']
      | NonNullable<PickupTimeItemsAggregationDetailFragment['items']>[number]
    )[]
  >
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onRefetch: (val?: Date) => Promise<void>
}

export const ConsoleQuantityManagementsTemplate: Template<State, Action> = ({
  state,
  onRefetch,
}) => {
  const { t } = useTranslation('consoleQuantityManagement')
  const { routerNames } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleQuantityManagements} />
      <ConsoleBaseLayout
        header={{
          back: true,
          title: t('title'),
          right: (
            <IconButton.Reload
              onClick={() => onRefetch()}
              edge="end"
              color="white"
              disabled={state.loading}
            />
          ),
        }}
      >
        <CollectionTitleCell
          eventName={state.eventName}
          loading={state.loading && !state.eventCollection}
        />
        {state.eventCollection ? (
          <FilterForm
            pickupDate={state.pickupDate}
            eventCollection={state.eventCollection}
            onSubmit={onRefetch}
          />
        ) : (
          <SkeletonFilterForm />
        )}
        <ReceiveTimeCell
          loading={state.loading}
          artistAggregatePickupTimeItems={state.artistAggregatePickupTimeItems}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type CollectionTitleCellProps = {
  eventName: string
  loading: boolean
}
const CollectionTitleCell: Cell<CollectionTitleCellProps> = ({
  loading,
  eventName,
}) => {
  return (
    <CellFrame sx={{ px: 5, py: 4 }}>
      {loading ? (
        <SkeletonText variant="h3" width="70%" />
      ) : (
        <Typography variant="h3">{eventName}</Typography>
      )}
    </CellFrame>
  )
}

type ReceiveTimeCellProps = {
  loading: State['loading']
  artistAggregatePickupTimeItems: State['artistAggregatePickupTimeItems']
}
const ReceiveTimeCell: Cell<ReceiveTimeCellProps> = ({
  loading,
  artistAggregatePickupTimeItems,
}) => {
  const { t } = useTranslation('consoleQuantityManagement')
  const { router, generatePath, routerPages } = useRouter()

  if (!loading && !artistAggregatePickupTimeItems?.length) {
    return (
      <CellFrame sx={{ px: 5, py: 6 }}>
        <Typography variant="body1" align="center" color="text.secondary">
          {t('pickupTimeItemEmptyMessage', '登録商品情報はありません')}
        </Typography>
      </CellFrame>
    )
  }

  return (
    <CellFrame sx={{ px: 5 }}>
      <TableContainer sx={{ mx: 'auto', pb: 5 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                align="left"
                sx={{ minWidth: 104, pt: 1, pb: 0, pl: 0, border: 'none' }}
              >
                <Typography variant="overline" color="text.hint">
                  {t('skuName', '商品名/SKU名')}
                </Typography>
              </TableCell>
              {['purchaseNumber', 'alreadyUsed', '残り'].map(item => (
                <TableCell
                  align="center"
                  sx={{ width: 70, pt: 1, pb: 0, pl: 0, border: 'none' }}
                  key={item}
                >
                  <Typography variant="overline" color="text.hint">
                    {t(item)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableScrollFetcher
            loading={loading}
            hasNextPage={false}
            onFetchMore={async () => {}}
            component={TableRow}
            colSpan={4}
            loadingComponent={
              <>
                {Array.from(Array(2).keys()).map(key => (
                  <Fragment key={key}>
                    <TableCell
                      sx={{ pl: 0, py: 1, border: 'none' }}
                      colSpan={4}
                    >
                      <SkeletonText variant="medium" width="50%" />
                    </TableCell>
                    {Array.from(Array(3).keys()).map(subKey => (
                      <TableRow key={subKey}>
                        <TableCell
                          component="th"
                          sx={{ pl: 0, py: 1 }}
                          scope="row"
                        >
                          <Stack
                            flex={1}
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Skeleton
                              variant="rectangular"
                              width={48}
                              height={48}
                            />
                            <Stack flex={1}>
                              <Stack>
                                <SkeletonText variant="small" width="100%" />
                              </Stack>
                              <Stack>
                                <SkeletonText variant="small" width="40%" />
                              </Stack>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <TableCell
                          sx={{ width: 70, pl: 0, py: 1 }}
                          align="center"
                        >
                          <SkeletonText variant="small" width="100%" />
                        </TableCell>
                        <TableCell
                          sx={{ width: 70, pl: 0, py: 1 }}
                          align="center"
                        >
                          <SkeletonText variant="small" width="100%" />
                        </TableCell>
                        <TableCell
                          sx={{ width: 70, pl: 0, py: 1 }}
                          align="center"
                        >
                          <SkeletonText variant="small" width="100%" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                ))}
              </>
            }
            nodes={artistAggregatePickupTimeItems?.map(
              (artistAggregatePickupTimeItem, index) => {
                if (typeof artistAggregatePickupTimeItem === 'string') {
                  return (
                    <Fragment key={index}>
                      <TableCell
                        sx={{ pl: 0, pb: 1, pt: 4, border: 'none' }}
                        colSpan={4}
                      >
                        <Typography variant="subtitle1">
                          {artistAggregatePickupTimeItem}
                        </Typography>
                      </TableCell>
                    </Fragment>
                  )
                }
                return (
                  <Fragment key={index}>
                    <TableCell component="th" sx={{ pl: 0, py: 1 }} scope="row">
                      <Stack
                        flex={1}
                        direction="row"
                        alignItems="center"
                        spacing={2}
                        onClick={() =>
                          router.push(
                            generatePath(routerPages.ConsoleItemDetail, {
                              itemId: artistAggregatePickupTimeItem.itemId,
                            })
                          )
                        }
                      >
                        <Avatar
                          variant="square"
                          src={
                            artistAggregatePickupTimeItem?.sku?.sku?.thumbnail
                              ?.smallUri ??
                            artistAggregatePickupTimeItem.item?.thumbnail?.front
                              ?.smallUri ??
                            ''
                          }
                          sx={{ width: 48, height: 48 }}
                        />
                        <Stack flex={1}>
                          <Typography variant="subtitle1">
                            {artistAggregatePickupTimeItem.item.title}
                          </Typography>
                          {!!artistAggregatePickupTimeItem.sku?.sku
                            ?.unitName && (
                            <Typography variant="caption">
                              {artistAggregatePickupTimeItem.sku.sku.unitName}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ pl: 0, py: 1 }} align="center">
                      <Typography variant="overline" color="text.secondary">
                        {artistAggregatePickupTimeItem?.sku?.count ??
                          artistAggregatePickupTimeItem.count ??
                          0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ pl: 0, py: 1 }} align="center">
                      <Typography variant="overline" color="text.secondary">
                        {artistAggregatePickupTimeItem?.sku?.completedCount ??
                          artistAggregatePickupTimeItem.completedCount ??
                          0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ pl: 0, py: 1 }} align="center">
                      <Typography variant="overline" color="text.secondary">
                        {/* 購入済と使用済みの差分を表示 */}
                        {(artistAggregatePickupTimeItem?.sku?.count ??
                          artistAggregatePickupTimeItem.count ??
                          0) -
                          (artistAggregatePickupTimeItem?.sku?.completedCount ??
                            artistAggregatePickupTimeItem.completedCount ??
                            0)}
                      </Typography>
                    </TableCell>
                  </Fragment>
                )
              }
            )}
          />
        </Table>
      </TableContainer>
    </CellFrame>
  )
}

const SkeletonFilterForm = () => {
  const { t } = useTranslation('consoleQuantityManagement')
  return (
    <CellFrame sx={{ px: 5 }}>
      <Typography variant="subtitle1">{t('receiptDate', '受取日')}</Typography>
      <Stack spacing={3} sx={{ pb: 4 }}>
        {Array.from(Array(2).keys()).map(key => (
          <Stack direction="row" spacing={2} key={key}>
            {Array.from(Array(4).keys()).map(subKey => (
              <Stack
                key={subKey}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: 14,
                  border: theme => `1px solid ${theme.palette.grey[100]}`,
                }}
              >
                <SkeletonText variant="small" width={40} />
              </Stack>
            ))}
          </Stack>
        ))}
      </Stack>
    </CellFrame>
  )
}
